'use client'

import { useWebHaptics } from 'web-haptics/react'
import { m, AnimatePresence } from 'motion/react'
import { DURATION, EASE_OUT_STRONG, SPRING } from '@/lib/motion-utils'
import Image from 'next/image'
import type { ClothingItem, Outfit } from '@/types/closet'

interface DeleteConfirmModalProps {
  item: ClothingItem | Outfit | null
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isDeleting?: boolean
  /** Shown in place of the warning line when a delete attempt failed. */
  error?: string
}

export function DeleteConfirmModal({
  item,
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
  error,
}: DeleteConfirmModalProps) {
  const haptic = useWebHaptics()

  if (!item) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: DURATION.exit } }}
            transition={{ duration: DURATION.enter }}
            className="fixed inset-0 z-[10000] bg-neutral-950/50"
            onClick={onClose}
          />
          <m.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
              opacity: 0,
              scale: 0.95,
              transition: { duration: DURATION.exit, ease: EASE_OUT_STRONG },
            }}
            transition={SPRING.modal}
            style={{ transformOrigin: 'center' }}
            className="fixed top-1/2 left-1/2 z-[10001] w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-neutral-950"
          >
            <div className="relative p-6">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 text-xs opacity-40 hover:opacity-100"
                disabled={isDeleting}
              >
                CLOSE
              </button>

              <div className="text-center">
                <h2 className="mb-6 text-sm">
                  DELETE {'items' in item ? 'OUTFIT' : 'ITEM'}?
                </h2>

                <div className="relative mx-auto mb-4 h-48 w-32 bg-zinc-100 dark:bg-zinc-900">
                  <Image
                    src={item.imageUrl || '/closet-base-image.png'}
                    alt={item.name}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover"
                  />
                </div>

                <p className="mb-1 text-xs uppercase">{item.name}</p>
                <p className="mb-6 text-xs text-zinc-400 uppercase dark:text-zinc-600">
                  {'category' in item ? item.category : 'outfit'}
                </p>

                {error ? (
                  <p
                    role="alert"
                    className="mb-6 text-xs text-red-600 dark:text-red-400"
                  >
                    {error}
                  </p>
                ) : (
                  <p className="mb-6 text-xs text-zinc-500 dark:text-zinc-400">
                    THIS ACTION CANNOT BE UNDONE.
                  </p>
                )}

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      haptic.trigger('warning')
                      onConfirm()
                    }}
                    disabled={isDeleting}
                    className="flex-1 border border-red-600 px-4 py-2 text-xs text-red-600 transition-colors hover:bg-red-600 hover:text-white disabled:opacity-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-400 dark:hover:text-black"
                  >
                    {isDeleting ? 'DELETING...' : 'CONFIRM'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 border border-zinc-300 px-4 py-2 text-xs transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
                    disabled={isDeleting}
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          </m.div>
        </>
      )}
    </AnimatePresence>
  )
}
